import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { KeyboardEvent } from 'react'
import { Icon, type IconName } from './Icon'

/**
 * Generic per-row overflow (kebab `⋮`) menu — the same affordance the
 * My-Leaves history table uses, extracted so every table's row actions read
 * consistently. Accessibility: aria-haspopup + role="menu", Escape closes and
 * restores focus, click-outside closes, ↑/↓ move between items, and the panel
 * flips above the trigger when it would overflow the viewport bottom.
 */
export interface RowAction {
  icon: IconName
  label: string
  onClick: () => void
  testId?: string
  destructive?: boolean
  disabled?: boolean
  /** Small muted sub-line under the label — e.g. why a disabled action is
   *  unavailable. Also surfaced as the item's title on hover. */
  hint?: string
}

export function RowActionsMenu({
  actions,
  ariaLabel = 'Row actions',
  testId,
}: {
  actions: ReadonlyArray<RowAction>
  ariaLabel?: string
  testId?: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [flipUp, setFlipUp] = useState(false)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent): void {
      if (
        panelRef.current &&
        triggerRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // The panel is portaled to <body> (so a table Card's `overflow: hidden`
  // can't clip it); track the trigger's rect for fixed positioning + flip.
  useEffect(() => {
    if (!open) return
    const measure = (): void => {
      const r = triggerRef.current?.getBoundingClientRect()
      if (!r) return
      setRect(r)
      const estimated = 8 + actions.length * 44
      setFlipUp(r.bottom + estimated > window.innerHeight)
    }
    measure()
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open, actions.length])

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  const close = useCallback((): void => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        close()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ??
            [],
        )
        if (items.length === 0) return
        const idx = items.findIndex((el) => el === document.activeElement)
        const next =
          e.key === 'ArrowDown'
            ? (idx + 1) % items.length
            : (idx - 1 + items.length) % items.length
        items[next]?.focus()
      }
    },
    [close],
  )

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={testId}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((prev) => !prev)
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          background: open ? 'var(--surface-2)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          color: 'var(--text-2)',
          transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--surface-2)'
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent'
        }}
      >
        <Icon name="more_vert" size={18} aria-hidden />
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              onKeyDown={handlePanelKeyDown}
              onClick={(event) => event.stopPropagation()}
              style={{
                position: 'fixed',
                // Right-align the panel to the trigger; flip above when it
                // would overflow the viewport bottom.
                right: Math.max(8, window.innerWidth - rect.right),
                ...(flipUp
                  ? { bottom: window.innerHeight - rect.top + 4 }
                  : { top: rect.bottom + 4 }),
                minWidth: 176,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                boxShadow: 'var(--shadow-2)',
                zIndex: 1000,
                overflow: 'hidden',
                padding: '6px 0',
                animation:
                  'lms-row-actions-in var(--motion-duration-fast) var(--motion-ease-out)',
              }}
            >
              <style>{`
                @keyframes lms-row-actions-in {
                  from { opacity: 0; transform: translateY(-4px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
                @media (prefers-reduced-motion: reduce) {
                  @keyframes lms-row-actions-in {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                  }
                }
              `}</style>
              {actions.map((action) => (
                <RowActionItem key={action.label} action={action} onDone={close} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

function RowActionItem({
  action,
  onDone,
}: {
  action: RowAction
  onDone: () => void
}): JSX.Element {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={action.testId}
      disabled={action.disabled}
      title={action.hint}
      onClick={() => {
        onDone()
        action.onClick()
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: action.hint ? 'flex-start' : 'center',
        gap: 10,
        padding: '8px 14px',
        background:
          hover && !action.disabled
            ? action.destructive
              ? 'color-mix(in srgb, var(--danger) 8%, transparent)'
              : 'var(--surface-2)'
            : 'transparent',
        border: 'none',
        cursor: action.disabled ? 'not-allowed' : 'pointer',
        opacity: action.disabled && !action.hint ? 0.5 : 1,
        color: action.destructive ? 'var(--danger)' : 'var(--text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        outline: 'none',
      }}
    >
      <Icon name={action.icon} size={16} aria-hidden style={{ marginTop: action.hint ? 2 : 0 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{ opacity: action.disabled ? 0.55 : 1 }}>{action.label}</span>
        {action.hint ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 400,
              color: 'var(--text-3)',
              whiteSpace: 'normal',
            }}
          >
            {action.hint}
          </span>
        ) : null}
      </span>
    </button>
  )
}
